import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, IconButton } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useLazyListSmsProvidersQuery } from "store/services/smsProviderApi";

import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import AppTable from "components/Shared/AppTable";
import CustomTabs from "components/Shared/CustomTabs";
import HighlightedText from "components/Shared/HighlightedText";

import { useAppMeta, useTablePagination } from "hooks";

const SmsProviderListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams, setSearchParams] = useSearchParams();

  // Set default tab to "Active" if not present
  React.useEffect(() => {
    if (!searchParams.get("tab")) {
      setSearchParams({ tab: "Active" }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";


  const breadcrumbs = [
    { title: "Settings", path: "/app/crm/setting/list" },
    { title: "SMS Provider" },
  ];
  const smsProviderTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const columns = [
    {
      name: "Name",
      selector: (row) => row.name,
      cell: (row) => (
        <HighlightedText text={row?.name || "N/A"} highlight={keyword} />
      ),
    },
    // {
    //   name: "Script Code",
    //   selector: (row) => row.scriptCode,
    //   cell: (row) => (
    //     <HighlightedText text={row?.scriptCode || "N/A"} highlight={keyword} />
    //   ),
    // },
    {
      name: "Country",
      selector: (row) => row.country?.name,
      cell: (row) => row?.country?.name || "N/A",
    },
    {
      name: "Created At",
      selector: (row) => row.createdAt,
      cell: (row) => <AppTableDateTime date={row?.createdAt} />,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/sms-provider/${row._id}/detail`}>
            <IconButton
              size="small"
              aria-label="View SMS Provider"
              color="primary"
            >
              <InfoOutlined />
            </IconButton>
          </Link>
          <Link to={`/app/crm/sms-provider/${row._id}/edit`}>
            <IconButton size="small" aria-label="Edit SMS Provider">
              <ModeEditOutlineTwoTone />
            </IconButton>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  // RTK Query
  const [
    fetchSmsProviders,
    { data: smsProviderRes, isLoading, isFetching, error },
  ] = useLazyListSmsProvidersQuery();

  React.useEffect(() => {
    fetchSmsProviders({
      active: activeTab === "Active",
      page: currentPage,
      limit: perPage,
      ...(keyword && { keyword }),
    });
  }, [activeTab, currentPage, fetchSmsProviders, keyword, perPage]);

  useAppMeta(`${activeTab} SMS Providers`);

  return (
    <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<PageActions />}>
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={smsProviderTabs} activeTab={activeTab} />

        <TableFilter
          searchLabel="Search SMS Providers"
          disabled={isLoading || isFetching}
        ></TableFilter>

        <AppTable
          columns={columns}
          data={smsProviderRes?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={smsProviderRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default SmsProviderListScreen;

const PageActions = () => {
  return (
    <Link to="/app/crm/sms-provider/create">
      <Button startIcon={<Add />} variant="contained">
        New SMS Provider
      </Button>
    </Link>
  );
};
